import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface SummaryCardsProps {
    data: any;
    isLoading: boolean;
    compareWithPrevious: boolean;
}

export function SummaryCards({
                                         data,
                                         isLoading,
                                         compareWithPrevious,
                                     }: SummaryCardsProps) {
    if (isLoading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="animate-pulse space-y-3">
                            <div className="h-4 bg-gray-200 rounded w-2/3" />
                            <div className="h-8 bg-gray-200 rounded w-full" />
                            <div className="h-3 bg-gray-200 rounded w-1/2" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (!data || !data.summary) {
        return null;
    }

    const formatMetricName = (metric: string): string => {
        const names: { [key: string]: string } = {
            impressions: 'Impressões',
            clicks: 'Cliques',
            revenue: 'Receita',
            ecpm: 'eCPM',
            ctr: 'CTR',
            active_view: 'Viewability',
            unfilled_impressions: 'Não Preenchidas',
            requests_served: 'Requisições',
            pmr: 'PMR',
        };
        return names[metric] || metric;
    };

    const formatValue = (metric: string, value: any): string => {
        if (value === null || value === undefined) return '-';

        switch (metric) {
            case 'revenue':
                return `$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            case 'ecpm':
                return `$ ${value.toFixed(2)}`;
            case 'ctr':
            case 'pmr':
            case 'active_view':
                return `${value.toFixed(2)}%`;
            case 'impressions':
            case 'clicks':
            case 'unfilled_impressions':
            case 'requests_served':
                return value.toLocaleString('pt-BR');
            default:
                return String(value);
        }
    };

    const getGrowthBadge = (change: any) => {
        if (!change) return null;

        const { change_percentage, trend } = change;

        if (trend === 'up') {
            return (
                <div className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-green-100 text-green-800 rounded-full">
                    <TrendingUp className="w-3 h-3" />
                    +{change_percentage.toFixed(1)}%
                </div>
            );
        } else if (trend === 'down') {
            return (
                <div className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-red-100 text-red-800 rounded-full">
                    <TrendingDown className="w-3 h-3" />
                    {change_percentage.toFixed(1)}%
                </div>
            );
        } else {
            return (
                <div className="flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full">
                    <Minus className="w-3 h-3" />
                    0%
                </div>
            );
        }
    };

    const getMetricIcon = (metric: string): string => {
        const icons: { [key: string]: string } = {
            revenue: '💰',
            impressions: '👁️',
            clicks: '🖱️',
            ecpm: '📊',
            ctr: '🎯',
            active_view: '📺',
            unfilled_impressions: '❌',
            requests_served: '📤',
            pmr: '✅',
        };
        return icons[metric] || '📈';
    };

    // Pegar as 4 primeiras métricas do sumário
    const summaryMetrics = Object.entries(data.summary).slice(0, 4);

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {summaryMetrics.map(([metric, values]: [string, any]) => (
                <div
                    key={metric}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                            <span className="text-2xl">{getMetricIcon(metric)}</span>
                            <span className="text-sm font-medium text-gray-600">
                {formatMetricName(metric)}
              </span>
                        </div>
                    </div>

                    {/* Valor Principal */}
                    <div className="text-3xl font-bold text-gray-900 mb-2">
                        {formatValue(metric, values.total)}
                    </div>

                    {/* Média */}
                    <div className="text-xs text-gray-500 mb-3">
                        Média: {formatValue(metric, values.average)}
                    </div>

                    {/* Comparação */}
                    {compareWithPrevious && data.comparison?.changes?.[metric] && (
                        <div className="pt-3 border-t border-gray-100">
                            {getGrowthBadge(data.comparison.changes[metric])}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}